import type { DefaultTheme } from 'styled-components';

// Transcribed from Figma "[DHCS Portal] DS Base" Buttons 303:35625, variant set 1322:91574 (2026-10-13):
// every value is the variable the variant binds. Focus is a state on every type, drawn by the
// "Focus" effect style (spread 4, Primary/background-active), not a type.

export type ButtonType =
  | 'primary'
  | 'secondary'
  | 'standout'
  | 'ghost'
  | 'subtle'
  | 'default'
  | 'destructive'
  | 'destructive-filled';

export type ButtonSize = 'M' | 'S' | 'XS';

export type ButtonState = 'default' | 'hover' | 'active' | 'disabled';

// styled-components declares DefaultTheme with `[key: string]: any`; dropping that index
// signature here keeps the augmented token names, so a misspelt token in the spec is a type
// error. Plain `theme.<key>` reads elsewhere are checked by `yarn tokens:keys` instead.
type KnownTheme = {
  [K in keyof DefaultTheme as string extends K ? never : K]: DefaultTheme[K];
};
type ThemeKey = keyof KnownTheme;

export type ColorToken = {
  [K in ThemeKey]: DefaultTheme[K] extends string ? K : never;
}[ThemeKey];

// Without a `typography` key the index signature would widen this to string; `never` makes a
// consumer theme that lacks it fail loudly instead.
export type TypeStyle = KnownTheme extends { typography: infer T } ? keyof T : never;

export interface ButtonColors {
  background: ColorToken | 'transparent';
  text: ColorToken;
  border?: ColorToken;
}

export const BUTTON_SPEC: Record<ButtonType, Record<ButtonState, ButtonColors>> = {
  // Primary and Destructive (Filled) bind the mode-constant Button/* tokens (DS Base answer
  // 14015:37743, DI-084), so their fills stay saturated with white labels in dark.
  primary: {
    default: { background: 'buttonPrimaryStrong', text: 'buttonOnStrong' },
    hover: { background: 'buttonPrimaryStrongHover', text: 'buttonOnStrong' },
    active: { background: 'buttonPrimaryStrongActive', text: 'buttonOnStrong' },
    disabled: { background: 'neutralBackground', text: 'neutralTextDisabled' },
  },
  secondary: {
    default: { background: 'neutralBackgroundBase', text: 'primaryText', border: 'neutralBorder' },
    hover: { background: 'primaryBackgroundStrong', text: 'primaryTextStrong' },
    active: { background: 'primaryBackgroundStrongActive', text: 'primaryTextStrongActive' },
    disabled: {
      background: 'neutralBackgroundBase',
      text: 'neutralTextDisabled',
      border: 'neutralBorder',
    },
  },
  standout: {
    default: { background: 'standoutBackground', text: 'standoutText' },
    hover: { background: 'standoutBackgroundHover', text: 'standoutText' },
    active: { background: 'standoutBackgroundActive', text: 'standoutText' },
    disabled: { background: 'neutralBackground', text: 'neutralTextDisabled' },
  },
  ghost: {
    default: { background: 'transparent', text: 'neutralTextWeak' },
    hover: { background: 'neutralBackgroundHover', text: 'neutralText' },
    active: { background: 'neutralBackgroundActive', text: 'neutralText' },
    disabled: { background: 'transparent', text: 'neutralTextDisabled' },
  },
  subtle: {
    default: { background: 'neutralBackground', text: 'neutralTextWeak' },
    hover: { background: 'neutralBackgroundHover', text: 'neutralText' },
    active: { background: 'neutralBackgroundActive', text: 'neutralText' },
    disabled: { background: 'transparent', text: 'neutralTextDisabled' },
  },
  default: {
    default: { background: 'primaryBackground', text: 'primaryText' },
    hover: { background: 'primaryBackgroundMedium', text: 'primaryTextHover' },
    active: { background: 'primaryBackgroundActive', text: 'primaryTextHover' },
    disabled: { background: 'neutralBackground', text: 'neutralTextDisabled' },
  },
  destructive: {
    default: { background: 'alertBackground', text: 'alertText' },
    hover: { background: 'alertBackgroundMedium', text: 'alertTextHover' },
    active: { background: 'alertBackgroundActive', text: 'alertTextHover' },
    disabled: { background: 'neutralBackground', text: 'neutralTextDisabled' },
  },
  'destructive-filled': {
    default: { background: 'buttonDestructiveStrong', text: 'buttonOnStrong' },
    hover: { background: 'buttonDestructiveStrongHover', text: 'buttonOnStrong' },
    active: { background: 'buttonDestructiveStrongActive', text: 'buttonOnStrong' },
    disabled: { background: 'neutralBackground', text: 'neutralTextDisabled' },
  },
};

// Figma effect style "Focus" on every State=Focus variant (e.g. 13563:40091), as the DS Base
// documentation 13574:37712 describes it: a solid 2px Focus/ring outside a 1px gap of the surface
// colour. It replaced the 4px Primary/background-active ring, which matched the Default type's
// Active fill and vanished on an open Default button.
export const BUTTON_FOCUS_RING: ColorToken = 'focusRing';
export const BUTTON_FOCUS_RING_WIDTH = 2;
export const BUTTON_FOCUS_GAP: ColorToken = 'neutralBackgroundBase';
export const BUTTON_FOCUS_GAP_WIDTH = 1;

export const buttonFocusRing = (theme: DefaultTheme): string =>
  `0 0 0 ${BUTTON_FOCUS_GAP_WIDTH}px ${theme[BUTTON_FOCUS_GAP]}, 0 0 0 ${
    BUTTON_FOCUS_GAP_WIDTH + BUTTON_FOCUS_RING_WIDTH
  }px ${theme[BUTTON_FOCUS_RING]}`;

export interface ButtonSizeSpec {
  height: number;
  paddingX: number;
  gap: number;
  radius: number;
  fontStyle: TypeStyle;
  iconSize: number;
}

export const BUTTON_SIZES: Record<ButtonSize, ButtonSizeSpec> = {
  M: { height: 40, paddingX: 10, gap: 8, radius: 6, fontStyle: 'actions14Sb', iconSize: 16 },
  S: { height: 32, paddingX: 8, gap: 6, radius: 4, fontStyle: 'actions14Sb', iconSize: 16 },
  XS: { height: 24, paddingX: 6, gap: 4, radius: 3, fontStyle: 'actions12Sb', iconSize: 16 },
};
